import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { money } from "@/lib/format";
import { loadLogo, receiptWhen } from "@/lib/receipt";

export const TYPES = [["tithes", "Tithe"], ["registrations", "Conference Registration"], ["donations", "Conference Collection"], ["other", "Other Collection"]];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const iso = (d) => d.toISOString().slice(0, 10);
const nice = (s) => { const [y, m, d] = s.split("-").map(Number); return `${MONTHS[m - 1].slice(0, 3)} ${d}, ${y}`; };

export function periodRange(kind, ref) {
  const [y, m, d] = ref.split("-").map(Number);
  if (kind === "year") return { start: `${y}-01-01`, end: `${y}-12-31`, label: `Year ${y}` };
  if (kind === "month") { const last = new Date(Date.UTC(y, m, 0)).getUTCDate(); return { start: `${y}-${String(m).padStart(2, "0")}-01`, end: `${y}-${String(m).padStart(2, "0")}-${last}`, label: `${MONTHS[m - 1]} ${y}` }; }
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - ((dt.getUTCDay() + 6) % 7));
  const start = iso(dt);
  dt.setUTCDate(dt.getUTCDate() + 6);
  const end = iso(dt);
  return { start, end, label: `Week of ${nice(start)} – ${nice(end)}` };
}

export function shiftRef(kind, ref, dir) {
  const [y, m, d] = ref.split("-").map(Number);
  if (kind === "year") return `${y + dir}-${String(m).padStart(2, "0")}-01`;
  if (kind === "month") return iso(new Date(Date.UTC(y, m - 1 + dir, 1)));
  return iso(new Date(Date.UTC(y, m - 1, d + 7 * dir)));
}

export function totalsByType(rows) {
  const out = TYPES.map(([k, label]) => ({ key: k, label, count: 0, total: 0 }));
  rows.forEach((r) => { const t = out.find((o) => o.key === r.payment_for); if (t) { t.count += 1; t.total += r.total || 0; } });
  return out;
}

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
const rowCells = (r) => [r.number, receiptWhen(r), r.payer_name, r.church_name, r.payment_label, r.event_name || "—", money(r.total)];
const HEAD = ["No.", "Date", "Paid by", "Church", "For", "Event", "Total"];

export async function printTotals({ dest, label, rows }) {
  const logo = await loadLogo();
  const types = totalsByType(rows);
  const grand = rows.reduce((a, r) => a + (r.total || 0), 0);
  const html = `<!doctype html><html><head><title>Receipt totals ${esc(dest)} ${esc(label)}</title><style>
@page{size:letter;margin:0.5in}body{font-family:Helvetica,Arial,sans-serif;font-size:10pt;color:#141414;margin:0}
.logo{text-align:center}.logo img{height:56pt}h1{text-align:center;font-size:17pt;margin:4pt 0 2pt}.sub{text-align:center;margin:0 0 12pt;color:#475569}
h2{font-size:12pt;margin:14pt 0 4pt}table{width:100%;border-collapse:collapse}td,th{border:1px solid #c8c8c8;padding:3pt 5pt;text-align:left}th{background:#f1f5f9}.r{text-align:right}tfoot td{font-weight:bold}tr{page-break-inside:avoid}
</style></head><body>${logo ? `<div class="logo"><img src="${logo.data}" alt="Logo"></div>` : ""}<h1>${esc(dest)} · Receipt Totals</h1><p class="sub">${esc(label)} · ${rows.length} receipt(s)</p>
<h2>Totals by payment type</h2><table><thead><tr><th>Payment type</th><th class="r">Receipts</th><th class="r">Total</th></tr></thead><tbody>${types.map((t) => `<tr><td>${t.label}</td><td class="r">${t.count}</td><td class="r">${money(t.total)}</td></tr>`).join("")}</tbody>
<tfoot><tr><td>GRAND TOTAL</td><td class="r">${rows.length}</td><td class="r">${money(grand)}</td></tr></tfoot></table>
<h2>Receipts</h2><table><thead><tr>${HEAD.map((h, i) => `<th${i === 6 ? ' class="r"' : ""}>${h}</th>`).join("")}</tr></thead><tbody>${rows.length ? rows.map((r) => `<tr>${rowCells(r).map((c, i) => `<td${i === 6 ? ' class="r"' : ""}>${esc(c)}</td>`).join("")}</tr>`).join("") : '<tr><td colspan="7">No receipts in this period.</td></tr>'}</tbody></table></body></html>`;
  const f = document.createElement("iframe");
  f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  document.body.appendChild(f);
  f.contentDocument.open();
  f.contentDocument.write(html);
  f.contentDocument.close();
  setTimeout(() => { f.contentWindow.focus(); f.contentWindow.print(); setTimeout(() => { f.remove(); window.focus(); }, 1000); }, 250);
}

export async function downloadTotalsPDF({ dest, label, rows }) {
  const logo = await loadLogo();
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = 612;
  let y = 36;
  if (logo) { const h = 50; const w = Math.min((h * logo.w) / logo.h, 200); doc.addImage(logo.data, logo.fmt, (W - w) / 2, y, w, (w * logo.h) / logo.w); y += (w * logo.h) / logo.w + 8; }
  doc.setFont("helvetica", "bold").setFontSize(16).text(`${dest} · Receipt Totals`, W / 2, y + 12, { align: "center" });
  doc.setFont("helvetica", "normal").setFontSize(10).text(`${label} · ${rows.length} receipt(s)`, W / 2, y + 28, { align: "center" });
  const types = totalsByType(rows);
  const grand = rows.reduce((a, r) => a + (r.total || 0), 0);
  const st = { fontSize: 9, cellPadding: 3, lineColor: [200, 200, 200], textColor: [20, 20, 20] };
  doc.setFont("helvetica", "bold").setFontSize(11).text("Totals by payment type", 36, y + 50);
  autoTable(doc, { startY: y + 56, margin: { left: 36, right: 36 }, theme: "grid", styles: st, headStyles: { fillColor: [241, 245, 249], fontStyle: "bold" },
    head: [["Payment type", "Receipts", "Total"]], body: types.map((t) => [t.label, t.count, money(t.total)]), foot: [["GRAND TOTAL", rows.length, money(grand)]],
    footStyles: { fillColor: [255, 255, 255], textColor: [20, 20, 20], fontStyle: "bold" }, columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
    didParseCell: (d) => { if (d.column.index > 0) d.cell.styles.halign = "right"; } });
  const y2 = doc.lastAutoTable.finalY + 20;
  doc.setFont("helvetica", "bold").setFontSize(11).text("Receipts", 36, y2);
  autoTable(doc, { startY: y2 + 6, margin: { left: 36, right: 36 }, theme: "grid", styles: st, headStyles: { fillColor: [241, 245, 249], fontStyle: "bold" },
    head: [HEAD], body: rows.length ? rows.map(rowCells) : [["No receipts in this period.", "", "", "", "", "", ""]],
    didParseCell: (d) => { if (d.column.index === 6) d.cell.styles.halign = "right"; } });
  doc.save(`Receipt-Totals-${dest.replace(/\s+/g, "-")}-${label.replace(/[^A-Za-z0-9]+/g, "-")}.pdf`);
}
